import { useContentMessages } from '@/features/editor/hooks/useContentMessages'
// Порт ref BadgeEditor.tsx:109-268 (строка списка): кнопка-попап с иконкой,
// поле текста и корзина. Ручку перетаскивания (ref `GripVertical`) не портируем —
// в рефе она ни к чему не подключена, а фейковые аффордансы у нас запрещены.
import { useState } from 'react'
import { ImageIcon, Plus } from 'lucide-react'
import { cn, Input, Popover, PopoverContent, PopoverTrigger } from '@teach/ui'
import type { DesignBadgeItem } from '@/types/blocks'
import { DESIGN_LIMITS } from '@/features/editor/lib/designElements'
import { getLucideIcon } from '@/features/editor/lib/iconCatalog'
import { RemoveItemButton } from '../shared/RemoveItemButton'
import { IconGrid } from './IconGrid'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  badge: DesignBadgeItem
  /** Текст текущего языка контента — локализацию разруливает BadgeList. */
  text: string
  canRemove: boolean
  onChange: (patch: Partial<DesignBadgeItem>) => void
  onTextChange: (text: string) => void
  onRemove: () => void
}

/** Превью в кнопке-триггере. Кастомную иконку показываем нейтральной заглушкой:
 *  произвольный URL автора в DOM формы не нужен (в канвасе он проходит проверку схемы). */
const IconPreview = ({ badge }: { badge: DesignBadgeItem }) => {
  if (badge.iconType === 'none') return <Plus className="h-3 w-3 text-muted-foreground" />
  if (badge.iconType === 'custom') return <ImageIcon className="h-3.5 w-3.5" />
  const Icon = getLucideIcon(badge.iconValue)
  return Icon ? <Icon className="h-3.5 w-3.5" /> : <Plus className="h-3 w-3" />
}

export const BadgeRow = ({ badge, text, canRemove, onChange, onTextChange, onRemove }: Props) => {
  const m = useMessages(messages)
  // Подсказки полей — на языке правки: видно, что правится английская версия.
  const cm = useContentMessages(messages)
  const [open, setOpen] = useState(false)

  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/50 p-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={m.badgeIcon}
            className={cn(
              'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border-2 border-dashed transition-colors',
              badge.iconType !== 'none'
                ? 'border-brand bg-brand/10'
                : 'border-border hover:border-foreground/20',
            )}
          >
            <IconPreview badge={badge} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-60 p-2" align="start">
          {/* Тот же выбор, что у бейджа на холсте: сразу сетка с поиском, первая
              клетка — «без иконки». */}
          <IconGrid
            value={badge.iconType === 'lucide' ? badge.iconValue : null}
            onPick={(iconValue) => {
              onChange({ iconType: 'lucide', iconValue })
              setOpen(false)
            }}
            onClear={() => {
              onChange({ iconType: 'none', iconValue: null })
              setOpen(false)
            }}
          />
        </PopoverContent>
      </Popover>

      <div className="relative flex-1">
        <Input
          value={text}
          onChange={(e) => onTextChange(e.target.value.slice(0, DESIGN_LIMITS.BADGE_TEXT))}
          className="h-8 pr-9 text-xs"
          placeholder={cm.badgeText}
          maxLength={DESIGN_LIMITS.BADGE_TEXT}
        />
        {/* Сколько символов ещё влезет. */}
        <span
          aria-label={m.charsLeftAria}
          className={cn(
            'pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] tabular-nums',
            DESIGN_LIMITS.BADGE_TEXT - text.length <= 3 ? 'text-warning' : 'text-muted-foreground',
          )}
        >
          {DESIGN_LIMITS.BADGE_TEXT - text.length}
        </span>
      </div>

      {canRemove ? <RemoveItemButton label={m.removeBadge} onClick={onRemove} /> : null}
    </div>
  )
}
